import React, { useState, useEffect, useContext, Fragment } from "react";
import { useRouter } from "next/router";
import { UserContext } from "../../contexts/UserContext";
import { DebounceInput } from "react-debounce-input";
import { Typeahead } from "react-bootstrap-typeahead";

import EventKit from "../../data/EventKit";
import LabelComponent from "../LabelComponent";
import AnnouncementKit from "../../data/AnnouncementKit";
import ChatMessageKit from "../../data/ChatMessageKit";
import { EventContext } from "../../contexts/EventContext";

import EventUserBrowserView from "./Views/EventUserView.BrowserView";
import EventUserMobileView from "./Views/EventUserView.MobileView";
import { labelSpacingPerView } from "./Views/components/utils";
import { renderContentByView } from "../Views/MobileClassRenderer";

export default function EventUserComponent() {
  const MAX_INVITES = 11;
  const MAX_FORM_FIELDS = 7;

  const [eventUserList, setEventUserList] = useState(null);
  const { globalEventData, setGlobalEventData } = useContext(EventContext);
  const [nextUrl, setNextUrl] = useState(null);
  const { me } = useContext(UserContext);

  const eventKit = new EventKit();
  const [isInviting, setIsInviting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMaxInviteModal, setShowMaxInviteModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [eventQuestionData, setEventQuestionData] = useState(null);
  const [filterData, setFilterData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidInvite, setIsInvalidInvite] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const typeaheads = [];

  const [personalMessage, setPersonalMessage] = useState("");
  const [eventUserCount, setEventUserCount] = useState(0);

  const router = useRouter();
  const { event } = router.query;
  const announcementKit = new AnnouncementKit();

  useEffect(() => {
    if (showModal) {
      fetchEventQuestions();
      fetchEventUserList(searchValue, filterData);
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal) {
      fetchEventUserList(searchValue, filterData);
    }
  }, [searchValue, filterData]);

  useEffect(() => {
    const selectedValuesCount = getFilteredSelectedValues().length;

    if (selectedValuesCount > MAX_INVITES) {
      //TODO: Implement popup
      handleMaxInviteModalTrigger();
      setIsInvalidInvite(true);
    } else if (selectedValuesCount === 0) {
      setIsInvalidInvite(true);
    } else {
      setIsInvalidInvite(false);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (!globalEventData) {
      eventKit.getEventDetail(event).then((res) => {
        const { data: eventData } = res;
        setGlobalEventData(eventData);
      });
    }
  }, [event]);

  function fetchEventUserList(searchTerm, filterData) {
    setIsLoading(true);
    setEventUserList(null);
    const preparedFilterData = {};
    if (filterData) {
      Object.entries(filterData).map((item) => {
        const questionSlug = item[0];
        const answers = item[1];
        if (answers) {
          const answersStringArray = answers.map((item) => item.value);
          preparedFilterData[questionSlug] = answersStringArray;
        }
      });
    }
    eventKit
      .getEventUserList(event, searchTerm, preparedFilterData)
      .then((data) => {
        setIsLoading(false);
        setNextUrl(data.next);
        setEventUserCount(data.count);
        const newEventUserList = filterNonUniqueUsers([
          ...getSelectedUsers(),
          ...data.results,
        ]);
        setEventUserList(newEventUserList);
      })
      .catch((errors) => console.log(errors));
  }

  function fetchEventUserLoadMore() {
    setIsLoading(true);
    eventKit
      .getEventUserListLoadMore(nextUrl)
      .then((data) => {
        setIsLoading(false);
        setNextUrl(data.next);
        setEventUserList((prevState) => prevState.concat(data.results));
      })
      .catch((errors) => console.log(errors));
  }

  function fetchEventQuestions() {
    eventKit.getEventQuestions(event).then((data) => {
      setEventQuestionData(data.results);
    });
  }

  function handleModalTriggerClick() {
    handleClearFilters();
    setShowModal((prevState) => !prevState);
  }

  function handleMaxInviteModalTrigger() {
    setShowMaxInviteModal((prevState) => !prevState);
  }

  function handleClearFilters() {
    const emptyValues = {};
    if (eventQuestionData) {
      eventQuestionData.forEach((question) => {
        emptyValues[question.slug] = [];
      });
      setFilterData(emptyValues);
    }
    typeaheads.forEach((typeahead) => {
      if (typeahead) {
        typeahead.clear();
      }
    });
    setSelectedValues({});
    setSearchValue("");
  }

  function renderTypeahead(question, canChooseMultiple = true) {
    const typeaheadAlternatives = question.alternatives.map((alternative) => {
      return {
        value: alternative.slug,
        label: alternative.title,
      };
    });

    if (question.kind === 0) {
      return (
        <div className={labelSpacingPerView} key={question.id}>
          <LabelComponent
            className={labelSpacingPerView}
            title={question.title}
          />
          <DebounceInput
            minLength={0}
            debounceTimeout={600}
            placeholder={question.title}
            onChange={(e) => {
              const value = e.target.value;
              const payload = value ? [{ value }] : [];
              setFilterData({ ...filterData, [question.slug]: payload });
            }}
            className="modal-search-bar mb-2 py-3"
          />
        </div>
      );
    } else {
      return (
        <div className={labelSpacingPerView} key={question.id}>
          <LabelComponent
            className={labelSpacingPerView}
            title={question.title}
          />
          <Typeahead
            id={`typeahead-${question.id}`}
            ref={(ref) => typeaheads.push(ref)}
            multiple={canChooseMultiple}
            options={typeaheadAlternatives}
            placeholder="Select"
            onChange={(selected) => {
              setFilterData({ ...filterData, [question.slug]: selected });
            }}
          >
            <div className="rbt-aux pt-1">
              <svg
                width="10"
                height="10"
                viewBox="0 0 14 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.292893 1.70711C-0.097631 1.31658 -0.0976312 0.683418 0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6.65685 5.24264C7.4379 6.02369 7.4379 7.29002 6.65685 8.07107L0.292893 1.70711Z"
                  fill="var(--vv-gray5)"
                />
                <path
                  d="M11.6066 0.292893C11.9971 -0.0976309 12.6303 -0.0976311 13.0208 0.292893C13.4113 0.683417 13.4113 1.31658 13.0208 1.70711L6.65685 8.07107C5.87581 7.29002 5.8758 6.02369 6.65685 5.24264L11.6066 0.292893Z"
                  fill="var(--vv-gray5)"
                />
              </svg>
            </div>
          </Typeahead>
        </div>
      );
    }
  }

  function handleChange(id, itemChecked) {
    setSelectedValues({ ...selectedValues, [id]: itemChecked });
  }

  function getFilteredSelectedValues() {
    return Object.entries(selectedValues)
      .filter((item) => {
        const value = item[1];
        return Boolean(value) === true;
      })
      .map((item) => {
        return Number(item[0]);
      });
  }

  function handleInviteToPrivateChatroom() {
    setIsInviting(true);
    const usersIdToInvite = getFilteredSelectedValues();
    ChatMessageKit.createPrivateChatroom({ eventSlug: event, usersIdToInvite })
      .then((data) => {
        handleModalTriggerClick();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsInviting(false));
  }

  function getSelectedUsers() {
    const mapArray = Object.values(selectedValues);
    return mapArray.filter((result) => Boolean(result));
  }

  function filterNonUniqueUsers(users) {
    const filteredUsers = [];
    users.forEach((user) => {
      const userInList = filteredUsers.findIndex(
        (userInList) => userInList.id === user.id
      );
      if (userInList < 0) filteredUsers.push(user);
    });

    return filteredUsers;
  }

  function getSearchResultCount() {
    const excludedEventUsersCount = eventUserList?.filter((item) => {
      return (
        item?.id === me?.id || !item?.user?.firstName || !item?.user?.lastName
      );
    }).length;

    return eventUserCount - excludedEventUsersCount;
  }

  const props = {
    handleModalTriggerClick,
    setShowModal,
    showModal,
    setSearchValue,
    eventQuestionData,
    renderTypeahead,
    handleInviteToPrivateChatroom,
    fetchEventUserLoadMore,
    getSelectedUsers,
    getFilteredSelectedValues,
    isLoading,
    eventUserList,
    eventUserCount,
    getSearchResultCount,
    handleChange,
    isInviting,
    isInvalidInvite,
    nextUrl,
    MAX_FORM_FIELDS,
    MAX_INVITES,
    me,
    selectedValues,
    hasProfilePicture: globalEventData.hasProfilePicture,
  };

  return (
    <>
      {renderContentByView(
        <EventUserMobileView {...props} />,
        <EventUserBrowserView {...props} />
      )}
    </>
  );
}
